import React from 'react';

export type TabName = 'map' | 'events' | 'match' | 'chat' | 'opportunities' | 'me';

interface Props {
  active: TabName;
  onChange: (t: TabName) => void;
  onStatusTap: () => void;
  unreadCount?: number;
  notifCount?: number;
  matchCount?: number;
}

const MapIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const EventsIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const MatchIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const ChatIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const OpportunitiesIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const MeIcon = ({ active }: { active: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={active ? 'text-stabeon-accent' : 'text-stabeon-muted'}>
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const TABS: { key: TabName; label: string; Icon: React.FC<{ active: boolean }> }[] = [
  { key: 'map',           label: 'Map',     Icon: MapIcon },
  { key: 'events',        label: 'Events',  Icon: EventsIcon },
  { key: 'match',         label: 'Connect', Icon: MatchIcon },
  { key: 'chat',          label: 'Chat',   Icon: ChatIcon },
  { key: 'opportunities', label: 'Offers', Icon: OpportunitiesIcon },
  { key: 'me',            label: 'Me',     Icon: MeIcon },
];

export function BottomNav({ active, onChange, onStatusTap, unreadCount = 0, notifCount = 0, matchCount = 0 }: Props) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[1000] !max-w-none" style={{ background: 'var(--nav-bg)', borderTop: '1px solid var(--nav-border)', boxShadow: '0 -4px 32px rgba(124,58,237,0.10)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="flex items-center justify-around px-2 h-16 max-w-2xl mx-auto">
        {/* Map + Events + People */}
        {TABS.slice(0, 3).map(t => (
          <NavItem key={t.key} tab={t} active={active} onChange={onChange} />
        ))}

        {/* Centre Status FAB */}
        <div className="relative -top-4">
          <button
            onClick={() => onStatusTap()}
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
            className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center text-white text-lg font-bold transition-opacity duration-150 active:opacity-75 btn-primary !p-0 !shadow-fab"
          >
            <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
              <span style={{ position: 'absolute', width: 20, height: 20, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', animation: 'stabeon-pulse 2s ease-out infinite' }} />
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
            </span>
          </button>
        </div>

        {/* Chat + Opportunities + Me */}
        {TABS.slice(3).map(t => (
          <NavItem
            key={t.key}
            tab={t}
            active={active}
            onChange={onChange}
            badge={
              t.key === 'chat' && unreadCount > 0 ? unreadCount :
              t.key === 'opportunities' && notifCount > 0 ? notifCount :
              t.key === 'match' && matchCount > 0 ? matchCount :
              undefined
            }
          />
        ))}
      </div>
    </nav>
  );
}

function NavItem({
  tab, active, onChange, badge,
}: { tab: typeof TABS[number]; active: TabName; onChange: (t: TabName) => void; badge?: number }) {
  const isActive = active === tab.key;
  return (
    <button
      onClick={() => onChange(tab.key)}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
      className={`relative flex flex-col items-center justify-center gap-0.5 min-w-[40px] min-h-[44px] px-2 rounded-2xl transition-all duration-150 active:scale-90 ${isActive ? 'bg-stabeon-accent/10' : ''}`}
    >
      <tab.Icon active={isActive} />
      <span className={`text-[10px] font-bold leading-none transition-all duration-150 ${isActive ? 'text-stabeon-accent opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
        {tab.label}
      </span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-0.5 right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 shadow-lg animate-pulse">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {isActive && <span className="nav-indicator" />}
    </button>
  );
}
